import React, { useMemo, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import '@/assets/styles/components/appointment-calendar.styles.scss';
import { CalendarDay, CalendarProps } from './appointment-calendar.types';
import { useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path';
import { Tooltip } from 'react-tooltip';
import { TooltipContent } from '@/screens/profile/_components';
import { AppointmentModal, Modal } from '@/components';
import { useQueryProvider } from '@/hooks';
import { useApi } from '@/api/useApi';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { TimeSpan } from '@/utility/time-span.ts';
import { Desktop, Mobile, useSizes } from '@/utility/responsive.ts';
import { ManualCalendarSlotUpdateRequestLiveServiceTypeEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { useFeatureFlag } from 'configcat-react';
import { useDevicePermissions } from '@/context/device-permission-context.tsx';

dayjs.locale('tr');

const DEFAULT_SLOT_TRESHOLD = 30;

const AppointmentCalendar: React.FC<CalendarProps> = ({ onDayClick, selectedDate, calendarData, onMonthChange, liveServiceType }) => {
	const [currentDate, setCurrentDate] = useState(dayjs());
	const navigation = useNavigate();
	const [showAppointmentModal, setShowAppointmentModal] = useState(false);
	const { CalendarControllerApi, SlotLimitControllerApi } = useApi();
	const { controlPermission } = useDevicePermissions();

	const { isMobile } = useSizes();
	const { value: isActiveGotItCreateAppointment } = useFeatureFlag('isactivegotitcreateappointment', true);
	const { value: isActiveCoachCreateAppointment } = useFeatureFlag('isactivecoachcreateappointment', true);

	const isCreateAppointmentButtonVisible = useMemo(() => {
		if (liveServiceType === ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.PlannedLecture) {
			return false;
		} else if (liveServiceType === ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.OneOnOneLecture) {
			return isActiveGotItCreateAppointment;
		} else {
			return isActiveCoachCreateAppointment;
		}
	}, [liveServiceType, isActiveGotItCreateAppointment, isActiveCoachCreateAppointment]);

	const [selectedDayIndex, setSelectedDayIndex] = useState<number | null>(null);

	useEffect(() => {
		if (isMobile && selectedDate) {
			const index = daysWithStates.findIndex(d => dayjs(d.date).isSame(selectedDate, 'day'));
			setSelectedDayIndex(index >= 0 ? index : 0);
		}
	}, [selectedDate, currentDate, isMobile]);

	useEffect(() => {
		const today = dayjs();
		const currentMonth = currentDate.month();
		const currentYear = currentDate.year();
		const todayMonth = today.month();
		const todayYear = today.year();

		if (currentMonth === todayMonth && currentYear === todayYear) {
			return;
		} else {
			const firstDayOfMonth = currentDate.startOf('month');

			if (onDayClick) {
				onDayClick(firstDayOfMonth.toDate(), {
					date: firstDayOfMonth.toDate(),
					isCurrentMonth: true,
					dayName: firstDayOfMonth.format('ddd'),
					state: 'first-day',
					hasAppointment: false,
					hasSlot: false
				});
			}
		}
	}, [currentDate.month(), currentDate.year()]);

	useEffect(() => {
		setTimeout(() => {
			const todayElement = document.querySelector('.calendar-day.today');
			if (todayElement && window.matchMedia('(max-width: 768px)').matches) {
				todayElement.scrollIntoView({
					behavior: 'auto',
					block: 'center',
					inline: 'start'
				});
			}
		}, TimeSpan.milliSecond.threeHundred);
	}, []);

	const generateDayStats = (date: dayjs.Dayjs): Partial<CalendarDay> => {
		const dateToCheck = dayjs(date);
		const today = dayjs();
		const formattedDate = dateToCheck.format('YYYY-MM-DD');
		const safeCalendarData = Array.isArray(calendarData) ? calendarData : [];
		const slotData = safeCalendarData.find(slot => slot.slotDate === formattedDate);

		if (slotData) {
			if (dateToCheck.isBefore(today, 'day')) {
				if (slotData.slotCount === 0) {
					return {
						hasAppointment: false,
						hasSlot: false
					};
				}

				return {
					stats: {
						value: `${slotData.appointmentCount}/${slotData.slotCount}`,
						isZero: slotData.appointmentCount === 0
					},
					hasAppointment: false,
					hasSlot: true
				};
			}

			if (slotData.slotCount === 0) {
				return {
					hasAppointment: true,
					hasSlot: false
				};
			}

			return {
				stats: {
					value: `${slotData.appointmentCount}/${slotData.slotCount}`,
					isZero: slotData.appointmentCount === 0
				},
				hasAppointment: false,
				hasSlot: true
			};
		}

		return {
			hasAppointment: false,
			hasSlot: false
		};
	};

	const { data: activeSlotCount } = useQueryProvider([QUERY_KEYS.getExpertActiveSlotCount], () => CalendarControllerApi.getExpertActiveSlotCount(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		staleTime: Infinity
	});

	const { data: slotLimits } = useQueryProvider([QUERY_KEYS.getSlotLimit, liveServiceType], () => SlotLimitControllerApi.getSlotLimit(liveServiceType), {
		select: ({ data }) => data.data,
		refetchOnMount: true
	});

	const slotTreshold = useMemo(() => {
		return slotLimits || DEFAULT_SLOT_TRESHOLD;
	}, [slotLimits]);

	const getDaysInMonth = useMemo(() => {
		const daysInMonth = currentDate.daysInMonth();
		const monthDays: CalendarDay[] = [];

		for (let i = 1; i <= daysInMonth; i++) {
			const currentDateInLoop = currentDate.date(i);
			monthDays.push({
				date: currentDateInLoop.toDate(),
				isCurrentMonth: true,
				dayName: currentDateInLoop.format('ddd'),
				...generateDayStats(currentDateInLoop)
			} as CalendarDay);
		}

		return monthDays;
	}, [currentDate, calendarData]);

	const daysWithStates = useMemo(() => {
		const today = dayjs();
		let firstActiveIndex = -1;
		let lastActiveIndex = -1;

		const activeStart = today;
		const activeEnd = today.add(12, 'day');

		const daysWithState = getDaysInMonth.map((day, index) => {
			const currentDay = dayjs(day.date);

			if (currentDay.isAfter(activeStart.subtract(1, 'day')) && currentDay.isBefore(activeEnd.add(1, 'day'))) {
				if (firstActiveIndex === -1) firstActiveIndex = index;
				lastActiveIndex = index;
			}

			if (currentDay.isBefore(today, 'day')) {
				return { ...day, state: 'past' };
			}

			if (currentDay.isAfter(activeStart.subtract(1, 'day')) && currentDay.isBefore(activeEnd.add(1, 'day'))) {
				if (currentDay.isSame(today, 'day')) {
					return { ...day, state: 'today active-period' };
				}
				return { ...day, state: 'active-period' };
			}

			if (currentDay.date() === 1) {
				return { ...day, state: 'first-day' };
			}

			return { ...day, state: 'inactive' };
		});

		return daysWithState.map((day, index) => {
			if (index === firstActiveIndex) {
				return { ...day, state: `${day.state} first-active` };
			}

			if (index === lastActiveIndex) {
				return { ...day, state: `${day.state} last-active` };
			}

			return day;
		});
	}, [getDaysInMonth, currentDate]);

	const navigateMonth = (direction: number) => {
		if (isMobile) {
			if (selectedDayIndex === null) return;
			const days = daysWithStates;
			const newIndex = selectedDayIndex + direction;

			if (newIndex < 0) {
				const prevMonth = currentDate.subtract(1, 'month');
				setCurrentDate(prevMonth);
				onMonthChange?.(prevMonth.toDate());

				setTimeout(() => {
					const prevMonthDays = dayjs(prevMonth).daysInMonth();
					setSelectedDayIndex(prevMonthDays - 1);
					const newDay = prevMonth.date(prevMonthDays).toDate();
					if (onDayClick) {
						const dayObj = {
							...daysWithStates[0],
							date: newDay,
							dayName: dayjs(newDay).format('ddd'),
							isCurrentMonth: true
						};
						onDayClick(newDay, dayObj);
					}
				}, 0);
			} else if (newIndex >= days.length) {
				const nextMonth = currentDate.add(1, 'month');
				setCurrentDate(nextMonth);
				onMonthChange?.(nextMonth.toDate());

				setTimeout(() => {
					setSelectedDayIndex(0);
					const newDay = nextMonth.date(1).toDate();
					if (onDayClick) {
						const dayObj = {
							...daysWithStates[0],
							date: newDay,
							dayName: dayjs(newDay).format('ddd'),
							isCurrentMonth: true
						};
						onDayClick(newDay, dayObj);
					}
				}, 0);
			} else {
				setSelectedDayIndex(newIndex);
				const newDay = days[newIndex];
				if (onDayClick && newDay) {
					onDayClick(newDay.date, newDay);
				}
			}
			return;
		}

		// Desktop flow
		const newDate = currentDate.add(direction, 'month');
		const today = dayjs();

		if (direction < 0) {
			const newMonth = newDate.month();
			const newYear = newDate.year();
			const currentMonth = today.month();
			const currentYear = today.year();

			if (newYear < currentYear || (newYear === currentYear && newMonth < currentMonth)) {
				return;
			}
		}

		const isNavigatingToCurrentMonth = newDate.month() === today.month() && newDate.year() === today.year();

		if (isNavigatingToCurrentMonth) {
			setCurrentDate(today);
			onMonthChange?.(today.toDate());

			if (onDayClick) {
				onDayClick(today.toDate(), {
					date: today.toDate(),
					isCurrentMonth: true,
					dayName: today.format('ddd'),
					state: 'today active-period',
					hasAppointment: false,
					hasSlot: false
				});
			}
		} else {
			setCurrentDate(newDate);
			onMonthChange?.(newDate.toDate());
		}
	};

	const goToToday = () => {
		const today = dayjs();
		setCurrentDate(today);
		onMonthChange?.(today.toDate());

		if (onDayClick) {
			onDayClick(today.toDate(), {
				date: today.toDate(),
				isCurrentMonth: true,
				dayName: today.format('ddd'),
				state: 'today active-period',
				hasAppointment: false,
				hasSlot: false
			});
		}

		setTimeout(() => {
			const todayElement = document.querySelector('.calendar-day.today');
			if (todayElement && isMobile) {
				todayElement.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'start'
				});
			}
		}, TimeSpan.milliSecond.oneHundred);
	};

	const isSelectedDay = useMemo(() => {
		if (isMobile && selectedDayIndex !== null) {
			return (date: Date, index: number) => index === selectedDayIndex;
		}
		return (date: Date) => dayjs(date).isSame(selectedDate, 'day');
	}, [selectedDate, isMobile, selectedDayIndex]);

	useEffect(() => {
		if (isMobile && selectedDayIndex !== null) {
			setTimeout(() => {
				const selectedEl = document.querySelector('.calendar-day.selected');
				if (selectedEl) {
					selectedEl.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
						inline: 'center'
					});
				}
			}, 100);
		}
	}, [selectedDayIndex, isMobile, currentDate]);

	const handleDayClick = (date: Date, day: CalendarDay) => {
		const today = dayjs();
		const clickedDay = dayjs(date);

		if (clickedDay.isBefore(today, 'day')) {
			return;
		}

		if (onDayClick) {
			onDayClick(date, day);
		}
		if (isMobile) {
			const index = daysWithStates.findIndex(d => dayjs(d.date).isSame(date, 'day'));
			setSelectedDayIndex(index);
		}
	};

	const onNavigateRecurringCalendar = () => {
		if (liveServiceType === ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.PlannedLecture) {
			navigation(NavigationPath.gotItPlannedRecurringCalendar);
		} else if (liveServiceType === ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.OneOnOneLecture) {
			navigation(NavigationPath.gotItRecurringCalendar);
		} else {
			navigation(NavigationPath.coachRecurringCalendar);
		}
	};

	const handleCreateAppointmentClick = () => {
		controlPermission(() => setShowAppointmentModal(true));
	};

	const handleCloseAppointmentModal = () => {
		setShowAppointmentModal(false);
	};

	const middleIndex = Math.ceil(daysWithStates.length / 2);
	const firstHalf = daysWithStates.slice(0, middleIndex);
	const secondHalf = daysWithStates.slice(middleIndex);

	return (
		<div className="calendar-wrapper">
			<div className="calendar-top">
				<div className="calendar-header">
					<div className="date-navigation">
						<h1>
							<span className="month">{currentDate.format('MMMM')}</span> <span className="year">{currentDate.format('YYYY')}</span>
						</h1>
						<div className="nav-buttons">
							<button onClick={() => navigateMonth(-1)}>
								<i className="icons-alt-arrow-left"></i>
							</button>
							<button
								className="mobile-today-button"
								onClick={() => goToToday()}>
								Bugün
							</button>
							<button onClick={() => navigateMonth(1)}>
								<i className="icons-alt-arrow-right"></i>
							</button>
						</div>
					</div>
					<div className="calendar-controls">
						<div className="action-buttons">
							<button
								className="repeating-calendar"
								onClick={onNavigateRecurringCalendar}>
								Tekrarlayan Takvim
							</button>
							<div className="appointment-button-wrapper">
								{isCreateAppointmentButtonVisible && (
									<button
										className="create-appointment"
										onClick={handleCreateAppointmentClick}>
										<i className="icons-add-circle"></i>
										Randevu Oluştur
									</button>
								)}
								{showAppointmentModal && (
									<>
										<Desktop>
											<div
												className="appointment-modal-container"
												onClick={e => e.stopPropagation()}>
												<AppointmentModal
													onClose={handleCloseAppointmentModal}
													liveServiceType={liveServiceType}
												/>
											</div>
										</Desktop>
										<Mobile>
											<Modal
												showCloseButton={true}
												className="appointment-modal-overlay"
												isOpen={showAppointmentModal}
												onClose={handleCloseAppointmentModal}>
												<AppointmentModal
													onClose={handleCloseAppointmentModal}
													liveServiceType={liveServiceType}
												/>
											</Modal>
										</Mobile>
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="calendar-grid-two-rows">
				<div className="calendar-row">
					{firstHalf.map((day, index) => (
						<div
							key={index}
							className={`calendar-day ${day.state} ${isSelectedDay(day.date, index) ? 'selected' : ''}`}
							onClick={() => handleDayClick(day.date, day)}>
							<div className="day-content">
								<div className="day-header">
									<span className="day-number">{day.date.getDate()}</span>
									<span className="day-name">{day.dayName}</span>
								</div>
								{day.hasSlot && day.stats && (
									<span
										className="day-stats"
										data-zero={day.stats.isZero}>
										{day.stats.value}
									</span>
								)}
								{day.hasAppointment && <i className="icons-calendar-add"></i>}
							</div>
						</div>
					))}
				</div>
				<div className="calendar-row">
					{secondHalf.map((day, index) => (
						<div
							key={index + middleIndex}
							className={`calendar-day ${day.state} ${isSelectedDay(day.date, index + middleIndex) ? 'selected' : ''}`}
							onClick={() => handleDayClick(day.date, day)}>
							<div className="day-content">
								<div className="day-header">
									<span className="day-number">{day.date.getDate()}</span>
									<span className="day-name">{day.dayName}</span>
								</div>
								{day.hasSlot && day.stats && (
									<span
										className="day-stats"
										data-zero={day.stats.isZero}>
										{day.stats.value}
									</span>
								)}
								{day.hasAppointment && <i className="icons-calendar-add"></i>}
							</div>
						</div>
					))}
				</div>
			</div>

			<div className="calendar-footer">
				<div className="info-item">
					<i
						className="icons-info-circle"
						data-tooltip-id="active-hours-info"></i>
					<Tooltip
						id="active-hours-info"
						className="stats-tooltip"
						place="top-start"
						render={() => (
							<TooltipContent
								items={[
									{
										value: `Aktif saatlerde minimum ${slotTreshold} saat açık olmalı`
									}
								]}
							/>
						)}
					/>
					Aktif günler açık saat: <span className="highlight">{activeSlotCount}</span>
				</div>
				<div className="mobile-action-buttons">
					<button
						className="repeating-calendar"
						onClick={onNavigateRecurringCalendar}>
						Tekrarlayan Takvim
					</button>
					{isCreateAppointmentButtonVisible && (
						<button
							className="create-appointment"
							onClick={handleCreateAppointmentClick}>
							<i className="icons-add-circle"></i>
							Randevu Oluştur
						</button>
					)}
				</div>
				{/*Will be open after product team approved*/}
				{/*<div className="monthly-stats">*/}
				{/*  <span className="bullet-point"></span>*/}
				{/*  Aylık iptal sayısı:{" "}*/}
				{/*  <span
            className={`highlight ${cancellationCount >= 5 ? "highlight-danger" : ""}`}
          >
            {cancellationCount}/5
          </span>
        </div>*/}
			</div>
		</div>
	);
};

export default AppointmentCalendar;
